import "./index.scss";

import React, { MouseEvent, useEffect, useState } from "react";
import { useEffectOnce, useEvent } from "../../../hooks";
import { useAppStore } from "../../../stores";
import { http } from "../../../utils/client";

import Search from "../../../forms/search";

const LOCALE_LABELS: Record<string, string> = {
	ky: "Кыргызча",
	ru: "Русский",
	en: "English",
};

const LOCALE_ORDER = ["ky", "ru", "en"];

type NavMenuItem = {
	_id: string;
	displayName: string;
	data: {
		link: string;
		contentTypeImage?: string;
		contentTypeImageAltText?: string;
		parentItem?: string;
		groups?: string;
		categoryLink?: string;
	};
	children?: NavMenuItem[];
};

type NavMenuList = NavMenuItem[];

interface HeaderTopProps {
	navMenuList?: NavMenuList;
}

export default function Component({ navMenuList }: HeaderTopProps) {
	const appStore = useAppStore((state) => state);
	const appStoreUpdate = useAppStore((state) => state.update);

	const [menuOpen, setMenuOpen] = useState(false);
	const [expandedIds, setExpandedIds] = useState<string[]>([]);

	const handleMenuToggle = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setMenuOpen((prev) => !prev);
	};

	const toggleAccordion = (e: React.MouseEvent<HTMLAnchorElement>, id: string, hasChildren: boolean) => {
		if (hasChildren) {
			e.preventDefault();
			e.stopPropagation();
			setExpandedIds((prev) => (prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]));
		} else {
			setMenuOpen(false);
		}
	};

	useEffect(() => {
		if (menuOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [menuOpen]);

	useEffectOnce(async () => {
		if (appStore.sitePath == null || appStore.appName == null || appStore.authData == null) return;

		try {
			const params = new URLSearchParams();

			params.set("id", `${appStore.authData.user.id}`);
			params.set("cookie", `${appStore.authData.cookie}`);

			const userResponse = await http({
				url: `${appStore.sitePath[0].link}/_/service/${appStore.appName}/user?${params.toString()}`,
			});

			if (userResponse.status === 401) throw new Error(`${userResponse.status} ${userResponse.statusText}`);
		} catch (e) {
			appStoreUpdate({ authData: null });
		}
	}, [appStore.sitePath, appStore.appName, appStore.authData, appStoreUpdate]);

	const handleBackdropClick = () => {
		setMenuOpen(false);
	};

	const handleMenuContentClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
	};

	useEvent(() => {
		setMenuOpen(false);
	});

	const sortedLocales = appStore.locales
		?.slice()
		.sort((a, b) => LOCALE_ORDER.indexOf(a.locale) - LOCALE_ORDER.indexOf(b.locale));

	function normalizePath(url: string) {
		return url
			.split("?")[0]
			.trim()
			.replace(/^\/[a-z]{2}(?=\/|$)/, "")
			.replace(/\/_\/error\/.*$/, "")
			.replace(/^$/, "");
	}

	return (
		<div className="header-top position-relative">
			<div className={`burger-menu ${menuOpen ? "open" : ""} d-md-none`} onClick={handleBackdropClick}>
				<div className="burger-menu-content" onClick={handleMenuContentClick}>
					<div className="burger-menu-header">
						<span className="me-auto fw-bold text-uppercase ps-2">Меню</span>
						<button className="btn text-white p-0" onClick={() => setMenuOpen(false)}>
							<i className="fa-solid fa-xmark fs-4"></i>
						</button>
					</div>

					<div className="burger-menu-scroll">
						<nav className="nav nav-menu col-12">
							{Array.isArray(navMenuList) && navMenuList.length > 0 ? (
								navMenuList.map((item) => {
									const hasChildren = item.children && item.children.length > 0;
									const isExpanded = expandedIds.includes(item._id);

									return (
										<div key={item._id} className="nav-block">
											<a
												className={`nav-link nav-link-text ${appStore.currentPage === item.data.link ? "active" : ""}`}
												href={item.data.link}
												onClick={(e) => toggleAccordion(e, item._id, !!hasChildren)}
											>
												<span>{item.displayName}</span>
												{hasChildren && (
													<i
														className={`fa-solid fa-chevron-down fs-6 text-muted opacity-50 arrow-icon ${
															isExpanded ? "rotate" : ""
														}`}
													></i>
												)}
											</a>

											{hasChildren && isExpanded && (
												<ul>
													{item.children!.map((child) => (
														<li key={child._id}>
															<a
																href={`${item.data.link}?category=${child.data?.categoryLink}`}
																onClick={() => setMenuOpen(false)}
															>
																{child.displayName}
															</a>
														</li>
													))}
												</ul>
											)}
										</div>
									);
								})
							) : (
								<div className="empty">
									<h3>{appStore.phrases?.["empty"]}</h3>
								</div>
							)}
						</nav>
					</div>
				</div>
			</div>

			<div className="container">
				<div className="d-flex gap-0 gap-md-4 align-items-center justify-content-end">
					<button className="btn menu-toggler d-md-none" onClick={handleMenuToggle}>
						<i className="fa-solid fa-bars"></i>
					</button>

					<div className="text-end d-flex justify-content-end justify-content-md-end align-items-center">
						<div className="d-md-inline-block w-100">
							<Search />
						</div>
					</div>

					<div className="dropdown">
						<button type="button" className="btn dropdown-toggle text-uppercase" data-bs-toggle="dropdown">
							{LOCALE_LABELS[appStore.locale] || appStore.locale}
						</button>

						<ul className="dropdown-menu dropdown-menu-end shadow border-0">
							{sortedLocales?.map((item) => {
								const normLink = normalizePath(item.link);
								return (
									<li key={`locale-${item.locale}`}>
										<a
											className={`dropdown-item d-flex gap-1 ${item.locale === appStore.locale ? "active" : ""}`}
											href={`/${item.locale}${normLink}`}
										>
											{LOCALE_LABELS[item.locale] || item.locale}
										</a>
									</li>
								);
							})}
						</ul>
					</div>

					<a id="specialButton" href="#" className="p-2" title="Версия для слабовидящих">
						<i className="fa-solid fa-eye-low-vision fs-5 text-white "></i>
					</a>
				</div>
			</div>
		</div>
	);
}
